import React, { useMemo } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '../../lib/utils';
import { ToolRegistry } from '../../core/tools/ToolRegistry';
import { IToolContext } from '../../core/tools/interfaces/ITool';
import { Separator } from '../ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  MoreVertical,
  Pencil,
  PanelRight,
  SwatchBook,
  ClipboardPaste,
  MessageSquarePlus,
  Link,
  Lock,
  Unlock,
  BringToFront,
  MoveUp,
  MoveDown,
  SendToBack,
  Copy,
  Trash2,
  GripVertical
} from 'lucide-react';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../ui/dropdown-menu';
import './ShapeToolbar.css';
import { ChromeMountPoint } from '../canvas/chrome/ChromeMountPoint';
import { ChromeInspector } from '../canvas/chrome/ChromeInspector';
import { useDiagramStore } from '../../stores/diagramStore';
import { useUIStore } from '../../stores/uiStore';
import { getCommandManager } from '../../hooks/useCommandManager';
import { ChangeNodeStyleCommand } from '../../core/commands/ChangeNodeStyleCommand';
import { CopyNodeCommand } from '../../core/commands/CopyNodeCommand';
import { getNodeGroupKey } from '../../lib/diagram/grouping';
import { Logger } from '../../utils/logger';
import { prepareShapeToolbarDelete } from './shapeToolbarDelete';
import { useShapeToolbarPlacement } from './useShapeToolbarPlacement';

const logger = Logger.getLoggerWithPrefix('ShapeToolbar');

interface ShapeToolbarProps {
  selectedShapes: any[];
  selectionBounds: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  toolRegistry: ToolRegistry;
  toolContext: IToolContext;
  onToolExecute?: (toolId: string, params?: any) => void | Promise<void>;
}

/**
 * Enhanced shape toolbar with all formatting tools in a single line.
 * Follows the exact UX specifications from ux-rules.md
 */
// TODO(ShapeToolbar menu follow-up): Temporarily removed menu actions until reimplemented.
// Hidden entries: Link to, Info, Move to layer, Copy as image, Duplicate, Export to CSV,
// Create frame, Save as template, Clear content.
let lastDragOffset = { x: 0, y: 0 };

type ShapeSwitchKey = string | null;

// TODO(emergent): migrate to canvas chrome manifests — trailing-cluster
// ordering should come from manifest.chrome.order via the registry, not this
// const (emergent-canvas-chrome plan §9, §12).
const SHAPE_TOOLBAR_TOOL_ORDER = [
  'shape-switch',    // Shape type switcher
  'connect',         // Start connector mode from the selected shape
  'bpmn-activity-style', // BPMN activity main style switcher
  'bpmn-activity-marker', // BPMN activity bottom marker switcher
  'bpmn-activity-task', // BPMN activity top-left task icon switcher
  'bpmn-event-style', // BPMN event ring style switcher
  'bpmn-event-trigger', // BPMN event trigger switcher
  'bpmn-gateway-type', // BPMN gateway variant switcher
  'group',           // Group/Ungroup
  'image-replace',   // Add/replace image for image/person nodes
  'shape-color',     // Shape fill color
  'shape-outline',   // Shape outline/border
  'font-picker',     // Font family picker (moved before size)
  'font-size',       // Font size numeric
  'text-style',      // Bold/Italic/Underline/Strikethrough
  'text-alignment',  // Text alignment
  'text-color'       // Text color
] as const;

const MANIFEST_SHAPE_TYPE_PREFIX = /^(aws|azure|gcp|k8s|generic|basic)_/;
const MANIFEST_STRUCTURED_LABEL_TOOL_IDS = new Set([
  'font-picker',
  'font-size',
  'text-style',
  'text-alignment',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function getShapeData(shape: unknown): Record<string, unknown> | null {
  if (!isRecord(shape)) return null;
  const getDataModel = shape.getDataModel;
  const data =
    (typeof getDataModel === 'function' && (getDataModel as () => unknown).call(shape)) ||
    shape.data ||
    shape;
  return isRecord(data) ? data : null;
}

function isManifestBackedBlueprintShape(shape: unknown): boolean {
  const data = getShapeData(shape);
  if (!data) return false;

  const attrs = isRecord(data.attrs) ? data.attrs : {};
  const type = typeof data.type === 'string' ? data.type : '';
  const manifestId =
    (typeof attrs._manifestId === 'string' && attrs._manifestId) ||
    (typeof attrs.manifestId === 'string' && attrs.manifestId) ||
    (typeof data.manifestId === 'string' && data.manifestId) ||
    type;

  return (
    typeof attrs.resourceType === 'string' ||
    typeof attrs.iconUrl === 'string' ||
    MANIFEST_SHAPE_TYPE_PREFIX.test(manifestId)
  );
}

function hasShapeLabelText(shape: unknown): boolean {
  const data = getShapeData(shape);
  if (!data) return false;
  const attrs = isRecord(data.attrs) ? data.attrs : {};
  const props = isRecord(data.props) ? data.props : {};
  const propsAttrs = isRecord(props.attrs) ? props.attrs : {};
  return [
    data.text,
    data.name,
    attrs.text,
    attrs.name,
    props.text,
    props.name,
    propsAttrs.text,
    propsAttrs.name,
  ].some((value) => typeof value === 'string');
}

const getShapeSwitchKey = (shape: any): ShapeSwitchKey => {
  const data =
    (typeof shape?.getDataModel === 'function' && shape.getDataModel()) || shape
  if (!data) return null;

  const nodeType = data.type as string | undefined;
  if (!nodeType) return null;

  const attrs = (data.attrs as Record<string, unknown>) || {};
  const variant =
    typeof attrs.variant === 'string'
      ? attrs.variant
      : typeof attrs.shapeType === 'string'
        ? attrs.shapeType
        : null;

  if (nodeType === 'person') {
    const sex = (data.sex as string | undefined) || (attrs.sex as string | undefined);
    const deceased = Boolean(data.deceased ?? attrs.deceased);
    const personVariant =
      typeof variant === 'string' && variant.length > 0
        ? variant
        : typeof sex === 'string' && sex.length > 0
          ? (deceased ? `deceased-${sex}` : sex)
          : 'unknown';
    return `person:${personVariant}`;
  }

  return typeof variant === 'string' && variant.length > 0 ? `${nodeType}:${variant}` : nodeType;
};

const hasUniformShapeSwitchSelection = (shapes: any[]): boolean => {
  if (shapes.length <= 1) return true;
  const keys = shapes.map(getShapeSwitchKey);
  if (keys.some((key) => !key)) return false;
  const [first] = keys;
  return keys.every((key) => key === first);
};

export const ShapeToolbar: React.FC<ShapeToolbarProps> = ({
  selectedShapes,
  selectionBounds,
  toolRegistry,
  toolContext,
  onToolExecute
}) => {
  // Hooks must be called unconditionally; pull store state before any early returns
  const { t } = useTranslation('diagram');
  const stage = useDiagramStore((state) => state.stage);
  const nodes = useDiagramStore((state) => state.nodes);
  const setActivePanel = useUIStore(s => s.setActivePanel);
  const setPropertyPanelUserClosed = useUIStore(s => s.setPropertyPanelUserClosed);

  // Track custom offset from drag (persisted across selections)
  const [dragOffset, setDragOffset] = React.useState(() => lastDragOffset);
  const [isDragging, setIsDragging] = React.useState(false);
  const dragStartPos = React.useRef({ x: 0, y: 0 });
  const handleForgetDragOffset = React.useCallback(() => {
    const resetOffset = { x: 0, y: 0 };
    lastDragOffset = resetOffset;
    setDragOffset(resetOffset);
  }, []);
  const { toolbarRef, placement } = useShapeToolbarPlacement({
    selectionBounds,
    stage,
    dragOffset,
    isDragging,
    onForgetDragOffset: handleForgetDragOffset,
  });

  const fallbackSelectionSummary = useMemo(() => {
    const summary = toolContext.selectionSummary;
    if (!summary || selectedShapes.length === 0) {
      return summary;
    }
    const hasLabelText = selectedShapes.some((shape: unknown) =>
      hasShapeLabelText(shape) || isManifestBackedBlueprintShape(shape),
    );

    if (summary.contextType !== 'none') {
      if (hasLabelText && summary.flags?.hasText !== true) {
        return {
          ...summary,
          flags: {
            ...(summary.flags || {}),
            hasText: true,
          },
        };
      }
      return summary;
    }

    const inferredTypes = new Set<string>();
    selectedShapes.forEach((shape: any) => {
      const data =
        (typeof shape?.getDataModel === 'function' && shape.getDataModel()) || shape;
      const nodeType = data?.type;
      const attrs = (data?.attrs as Record<string, unknown> | undefined) || {};
      const category = typeof attrs.category === 'string' ? attrs.category : undefined;
      if (nodeType === 'text') inferredTypes.add('text');
      else if (nodeType === 'sticky-note' || category === 'sticky-note') inferredTypes.add('sticky-note');
      else if (nodeType === 'image') inferredTypes.add('image');
      else if (nodeType === 'pdf') inferredTypes.add('pdf');
      else if (nodeType === 'frame') inferredTypes.add('frame');
      else if (nodeType === 'person') inferredTypes.add('person');
      else inferredTypes.add('genericShape');
    });

    const inferredContextType =
      inferredTypes.size === 1
        ? (Array.from(inferredTypes)[0] as any)
        : inferredTypes.size > 1
          ? 'mixed'
          : 'none';

    const inferredCountsByType: Record<string, number> = {};
    selectedShapes.forEach((shape: any) => {
      const data =
        (typeof shape?.getDataModel === 'function' && shape.getDataModel()) || shape;
      const nodeType = data?.type;
      const attrs = (data?.attrs as Record<string, unknown> | undefined) || {};
      const category = typeof attrs.category === 'string' ? attrs.category : undefined;
      const key =
        nodeType === 'text'
          ? 'text'
          : nodeType === 'sticky-note' || category === 'sticky-note'
            ? 'sticky-note'
            : nodeType === 'image'
              ? 'image'
              : nodeType === 'pdf'
                ? 'pdf'
              : nodeType === 'frame'
                ? 'frame'
                : nodeType === 'person'
                  ? 'person'
                  : 'genericShape';
      inferredCountsByType[key] = (inferredCountsByType[key] || 0) + 1;
    });

    return {
      ...summary,
      contextType: inferredContextType,
      typesSet: inferredTypes as any,
      countsByType: inferredCountsByType,
      flags: {
        ...(summary.flags || {}),
        hasText: hasLabelText || selectedShapes.some((shape: any) => {
          const data =
            (typeof shape?.getDataModel === 'function' && shape.getDataModel()) || shape;
          return Boolean(data?.type === 'text' || data?.type === 'sticky-note');
        }),
      },
    };
  }, [selectedShapes, toolContext.selectionSummary]);

  const effectiveToolContext = useMemo<IToolContext>(() => {
    const withFallbackSummary =
      fallbackSelectionSummary === toolContext.selectionSummary
        ? toolContext
        : ({ ...toolContext, selectionSummary: fallbackSelectionSummary } as IToolContext);

    const selectedFromContext = (() => {
      try {
        return withFallbackSummary.getSelectedShapes?.() ?? [];
      } catch {
        return [];
      }
    })();

    if (selectedFromContext.length > 0 || selectedShapes.length === 0) {
      return withFallbackSummary;
    }

    // When selection IDs are ahead of shape-map hydration, provide the selected nodes
    // as toolbar shape objects so tool popovers (e.g. switch-shape) can render immediately.
    const normalizedSelectedShapes = selectedShapes.map((shape: any) => ({ ...shape, data: shape }));
    return {
      ...withFallbackSummary,
      getSelectedShapes: () => normalizedSelectedShapes as any[],
    };
  }, [fallbackSelectionSummary, selectedShapes, toolContext]);

  // Get tools by id in stable order
  const orderedTools = useMemo(() => {
    if (selectedShapes.length === 0) return [] as any[];

    // Debug logging for sticky notes
    if (toolContext.selectionSummary?.contextType === 'sticky-note') {
      logger.debug('Sticky note selection', {
        selectionSummary: toolContext.selectionSummary,
        selectedShapes
      });
    }

    let list = toolRegistry.getToolsForLocation('floatingPanel', fallbackSelectionSummary);

    if (selectedShapes.length > 1 && toolContext.selectionSummary?.diagramType === 'org') {
      list = list.filter(t => t.manifest.id !== 'shape-switch');
    } else if (selectedShapes.length > 1 && !hasUniformShapeSwitchSelection(selectedShapes)) {
      list = list.filter(t => t.manifest.id !== 'shape-switch');
    }

    // Hide fill color for freehand selections (freehand doesn't have a fill)
    try {
      const hasFreehand = selectedShapes.some((s: any) => s?.type === 'freehand');
      if (hasFreehand) {
        list = list.filter(t => t.manifest.id !== 'shape-color');
      }
    } catch {}

    // Blueprint manifest shapes are structured cards/containers. They support
    // card-shell and label-colour styling, but the renderer owns typography
    // metrics so resource labels keep their provider-card layout.
    try {
      const hasManifestShape = selectedShapes.some(isManifestBackedBlueprintShape);
      if (hasManifestShape) {
        list = list.filter(t => !MANIFEST_STRUCTURED_LABEL_TOOL_IDS.has(t.manifest.id));
      }
    } catch {}

    // Org chart nodes manage alignment programmatically; hide alignment control for org selections.
    try {
      const isOrgDiagram = toolContext.selectionSummary?.diagramType === 'org';
      const hasOrgChartSelection = selectedShapes.some((s: any) => {
        const type = typeof s?.type === 'string' ? s.type : '';
        const category = (s?.category as string | undefined) ?? (s?.attrs as any)?.category;
        return category === 'orgchart' || type.startsWith('org_');
      });
      if (isOrgDiagram && hasOrgChartSelection) {
        list = list.filter(t => t.manifest.id !== 'text-alignment');
      }
    } catch {}

    if (toolContext.selectionSummary?.contextType === 'sticky-note') {
      logger.debug('Tools for sticky note', {
        toolCount: list.length,
        toolIds: list.map(t => t.manifest.id)
      });
    }

    const byId = new Map(list.map(t => [t.manifest.id, t]));
    const tools: any[] = [];
    for (const id of SHAPE_TOOLBAR_TOOL_ORDER) {
      const t = byId.get(id);
      if (t && t.canExecute(effectiveToolContext)) tools.push(t);
    }
    return tools;
  }, [selectedShapes, toolRegistry, toolContext, fallbackSelectionSummary, effectiveToolContext]);

  // Group boundaries: 1) shape switching 2) colours 3) typography
  const groupedTools = useMemo(() => {
    const byId = new Map(orderedTools.map(t => [t.manifest.id, t]));
    const group1 = [
      'shape-switch',
      'connect',
      'bpmn-activity-style',
      'bpmn-activity-marker',
      'bpmn-activity-task',
      'bpmn-event-style',
      'bpmn-event-trigger',
      'bpmn-gateway-type',
      'group',
      'image-replace',
      'shape-color',
      'shape-outline',
      'text-color',
    ]
      .map(id => byId.get(id))
      .filter(Boolean) as any[];
    const group3 = ['font-picker', 'font-size', 'text-alignment', 'text-style']
      .map(id => byId.get(id))
      .filter(Boolean) as any[];
    return [group1, group3].filter(g => g.length > 0);
  }, [orderedTools]);

  // Determine if entire selection is locked (must be before early return)
  const allLocked = useMemo(() => {
      if (!selectedShapes || selectedShapes.length === 0) return false;
      return selectedShapes.every((s: any) => !!(s?.locked || s?.attrs?.locked));
  }, [selectedShapes]);

  // Handle drag move and end - MUST be before early return to satisfy Rules of Hooks
  React.useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStartPos.current.x;
      const deltaY = e.clientY - dragStartPos.current.y;
      setDragOffset(prev => {
        const next = {
          x: prev.x + deltaX,
          y: prev.y + deltaY
        };
        lastDragOffset = next;
        return next;
      });
      dragStartPos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Handle tool execution
  const handleToolExecute = async (tool: any, params?: any) => {
    try {
      if (onToolExecute) {
        const maybePromise = onToolExecute(tool.manifest.id, params);
        if (maybePromise instanceof Promise) {
          await maybePromise;
        }
      } else {
        await toolRegistry.executeTool(tool.manifest.id, params);
      }
    } catch (error) {
      logger.error(`Failed to execute tool ${tool.manifest.id}:`, error);
    }
  };

  const handleAddComment = async () => {
    const commentsTool = toolRegistry.getTool('comments');
    if (!commentsTool) {
      logger.warn('Comments tool is not registered; cannot add comment from shape toolbar.');
      return;
    }

    await handleToolExecute(commentsTool);
  };

  // Edit via canvas event: resolves the target Text node and opens overlay
  const handleMenuEdit = () => {
    try {
      const first = selectedShapes[0];
      if (!first) return;
      const field: 'name' | 'location' | 'text' =
        first.type === 'person' || typeof first.attrs?.name === 'string' ? 'name' : 'text';
      (toolContext.eventBus as any)?.emit?.('canvas:text:edit', { nodeId: first.id, field });
    } catch (err) {
      logger.error('Edit failed', err);
    }
  };

  const handleOpenPropertiesPanel = () => {
    try {
      setActivePanel('properties');
      setPropertyPanelUserClosed(false);
    } catch (err) {
      logger.error('Failed to open properties panel from shape toolbar', err);
    }
  };

  // In-memory style clipboard (page scoped) – share across toolbar uses
  const styleClipboard: { styles?: Record<string, any> } = (window as any).__STYLE_CLIPBOARD__ || ((window as any).__STYLE_CLIPBOARD__ = {});

  // Collect normalized styles from a shape (fill, outline, text)
  const collectStyles = (shape: any): Record<string, any> => {
    const attrs = (shape?.attrs || {}) as Record<string, any>;
    return {
      fillColor: shape.fillColor ?? attrs.fillColor,
      strokeColor: shape.strokeColor ?? attrs.strokeColor,
      strokeWidth: shape.strokeWidth ?? attrs.strokeWidth ?? attrs.borderWidth,
      dash: Array.isArray(attrs.dash) ? attrs.dash : (Array.isArray((attrs as any).borderDash) ? (attrs as any).borderDash : undefined),
      dashEnabled: typeof attrs.dashEnabled === 'boolean' ? attrs.dashEnabled : undefined,
      fontFamily: attrs.fontFamily ?? shape.fontFamily,
      fontSize: attrs.fontSize ?? shape.fontSize,
      fontWeight: attrs.fontWeight,
      fontStyle: attrs.fontStyle,
      textDecoration: attrs.textDecoration,
      textAlign: attrs.textAlign,
      verticalAlign: attrs.verticalAlign,
      textColor: attrs.textColor,
    };
  };

  const handleCopyStyle = async () => {
    const first = selectedShapes[0];
    if (!first) return;
    const styles = collectStyles(first);
    styleClipboard.styles = styles;
    try { await navigator.clipboard?.writeText?.(JSON.stringify({ type: 'creately-style', styles })); } catch {}
  };

  const handlePasteStyle = async () => {
    const cm = getCommandManager();
    let styles = styleClipboard.styles;
    if (!styles) {
      try {
        const text = await navigator.clipboard?.readText?.();
        const parsed = JSON.parse(text || '{}');
        if (parsed && parsed.type === 'creately-style') styles = parsed.styles as Record<string, any>;
      } catch {}
    }
    if (!styles) return;
    const ids = selectedShapes.map(s => s.id);
    const apply = async (key: string, value: any) => {
      if (value === undefined) return;
      try {
        await cm.execute(new ChangeNodeStyleCommand(ids, key, value));
      } catch (err) {
        logger.warn('Paste style failed', key, err);
      }
    };
    await apply('fillColor', styles.fillColor);
    await apply('strokeColor', styles.strokeColor);
    await apply('strokeWidth', styles.strokeWidth);
    await apply('dash', styles.dash);
    await apply('dashEnabled', styles.dashEnabled);
    await apply('fontFamily', styles.fontFamily);
    await apply('fontSize', styles.fontSize);
    await apply('fontWeight', styles.fontWeight);
    await apply('fontStyle', styles.fontStyle);
    await apply('textDecoration', styles.textDecoration);
    await apply('textAlign', styles.textAlign);
    await apply('verticalAlign', styles.verticalAlign);
    await apply('textColor', styles.textColor);
  };

  const handleCopyLink = async () => {
    const first = selectedShapes[0];
    if (!first) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('shape', first.id);
      await navigator.clipboard.writeText(url.toString());
    } catch {}
  };

  // Z-index handlers
  const handleZ = async (mode: 'front'|'forward'|'backward'|'back') => {
    try {
      const { AdjustZIndexCommand } = await import('../../core/commands/AdjustZIndexCommand');
      const ids = selectedShapes.map(s => s.id);
      const cmd = new (AdjustZIndexCommand as any)(ids, mode);
      const cm = getCommandManager();
      await cm.execute(cmd);
    } catch (err) {
      logger.error('Z-index change failed', err);
    }
  };

  const handleCopySelection = async () => {
    const selectedIds = Array.from(effectiveToolContext.selection || new Set<string>());
    if (selectedIds.length === 0) return;

    const commandManager = getCommandManager();
    try {
      await commandManager.execute(new CopyNodeCommand(selectedIds));
    } catch (err) {
      logger.error('Copy command failed', err);
      return;
    }

  };

  const handleDeleteSelection = async () => {
    const deleteTool = prepareShapeToolbarDelete({
      toolRegistry,
      toolContext: effectiveToolContext,
      selectedShapeIds: selectedShapes
        .map((shape) => shape?.id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0),
    });
    if (!deleteTool) {
      logger.warn('Delete tool is not registered; cannot delete selection from toolbar.');
      return;
    }

    try {
      await handleToolExecute(deleteTool);
    } catch (err) {
      logger.error('Delete command failed', err);
    }
  };

//   // Deep link helpers
//   const makeShapeLink = (shapeId: string) => {
//     const w = window as unknown as { __DIAGRAM_ID__?: string };
//     const diagramId = w.__DIAGRAM_ID__ || new URLSearchParams(window.location.search).get('diagram') || '';
//     const url = new URL(window.location.href);
//     if (diagramId) url.searchParams.set('diagram', diagramId);
//     url.searchParams.set('shape', shapeId);
//     return url.toString();
//   };

//   const handleCopyLink = async () => {
//     const first = selectedShapes[0];
//     if (!first) return;
//     const link = makeShapeLink(first.id);
//     try { await navigator.clipboard.writeText(link); } catch {}
//   };

//   const handleLinkTo = async () => {
//     const first = selectedShapes[0];
//     if (!first) return;
//     let initial = '';
//     try { initial = await navigator.clipboard.readText(); } catch {}
//     const input = window.prompt('Add link and hit enter', initial || '');
//     if (!input) return;
//     const cm = getCommandManager();
//     try { await cm.execute(new ChangeNodeStyleCommand([first.id], 'link', input)); } catch (err) { logger.error('Failed to set link', err); }
//   };

  const hideEditAndStyleClipboardForImages =
    selectedShapes.length > 0 &&
    selectedShapes.every((s: { type?: string }) => s?.type === 'image');

  // Don't render if no selection
  if (selectedShapes.length === 0 || orderedTools.length === 0) {
    return null;
  }

  // Handle drag start
  const handleDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartPos.current = { x: e.clientX, y: e.clientY };
  };

  return (
    <div
      ref={toolbarRef}
      data-testid="tid.editor.shapeToolbar"
      className={cn(
        "shape-toolbar",
        "absolute z-50",
        "bg-background rounded-[10px]", // radius 10 per UX rules
        "border border-border", // border color per UX rules
        "shadow-[0_6px_18px_rgba(2,6,23,0.08)]", // elevation per UX rules
        "grid grid-flow-col auto-cols-max", // Single line layout with minimal gaps
        "min-w-fit"
      )}
      style={{
        left: placement.centerX,
        top: placement.topY,
        transform: 'translateX(-50%)',
      }}
    >
      {/* Grab handle */}
      <div
        className={cn(
          "flex items-center justify-center py-2 pl-1.5 pr-1 bg-muted/40 rounded-l-[10px]",
          isDragging ? "cursor-grabbing" : "cursor-grab"
        )}
        onMouseDown={handleDragStart}
        role="button"
        tabIndex={0}
        aria-label={t('shapeToolbar.dragToReposition', { defaultValue: 'Drag to reposition toolbar' })}
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" aria-hidden />
      </div>

      <Separator orientation="vertical" className="w-[1px] bg-transparent border-r-[1px] border-solid border-[#E5E7EB] mr-2" />

      {/* Leading cluster: registry-driven selection verbs (emergent canvas
          chrome, plan §5). Renders nothing when no manifests pass the gates,
          so non-Atlas domains see the toolbar unchanged. */}
      <ChromeMountPoint
        mount="selectionAction"
        toolRegistry={toolRegistry}
        onToolExecute={onToolExecute}
        className="py-1"
        trailing={
          <Separator
            orientation="vertical"
            className="w-[1px] bg-transparent border-r-[1px] border-solid border-[#E5E7EB] mx-2"
          />
        }
      />

      {/* Render grouped tools with dividers between groups */}
      {groupedTools.map((group, gi) => (
        <React.Fragment key={`group-${gi}`}>
          {gi > 0 && (
            <Separator
              orientation="vertical"
              className="w-[1px] bg-transparent border-r-[1px] border-solid border-[#E5E7EB] mx-2"
            />
          )}
          {group.map((tool) => {
            const ToolComponent = tool.getFloatingPanelComponent?.() || tool.getToolbarComponent?.();
            if (!ToolComponent) return null;
            const suppressToolTooltip =
              tool.manifest.id === 'table-grid'
              || tool.manifest.id === 'table-cell-style'
              || tool.manifest.id === 'image-replace';
            if (suppressToolTooltip) {
              return (
                <div key={tool.manifest.id} className="flex-shrink-0 py-2 px-0" data-testid={`tid.editor.tool.${tool.manifest.id}`}>
                  <ToolComponent
                    tool={tool}
                    context={effectiveToolContext}
                    onExecute={(params: any) => handleToolExecute(tool, params)}
                  />
                </div>
              );
            }
            const toolLabel = (() => {
              if (tool.manifest.id !== 'group') {
                return t(tool.manifest.name) || (tool.manifest.id?.replace(/-/g, ' ') || 'Tool');
              }
              const selectedNodeIds = Array.from(effectiveToolContext.selection || new Set<string>())
                .filter((id) => nodes.some((n: any) => n.id === id));
              if (selectedNodeIds.length < 2) return t('tools.group');
              const keys = new Set<string>();
              selectedNodeIds.forEach((id) => {
                const node = nodes.find((n: any) => n.id === id);
                const key = getNodeGroupKey(node);
                if (!key) {
                  keys.add('__ungrouped__');
                  return;
                }
                keys.add(key);
              });
              if (keys.size === 1 && !keys.has('__ungrouped__')) {
                return t('tools.ungroup');
              }
              return t('tools.group');
            })();
            return (
              <TooltipProvider key={tool.manifest.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex-shrink-0 py-2 px-0" data-testid={`tid.editor.tool.${tool.manifest.id}`}>
                      <ToolComponent
                        tool={tool}
                        context={effectiveToolContext}
                        onExecute={(params: any) => handleToolExecute(tool, params)}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>{toolLabel}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </React.Fragment>
      ))}

      {/* Add separator before 3-dot menu */}
      <Separator orientation="vertical" className="w-[1px] bg-transparent border-r-[1px] border-solid border-[#E5E7EB] ml-2 mr-1" />

      {/* 3-dot context menu */}
      <TooltipProvider>
        <DropdownMenu>
          <Tooltip>
            <DropdownMenuTrigger asChild>
              <TooltipTrigger asChild>
                <div className="flex-shrink-0 py-2 pr-2 pl-1">
                  <button className="w-6 h-8 flex items-center justify-center hover:bg-muted rounded transition-colors" data-testid="tid.editor.shapeToolbar.more">
                    <MoreVertical className="h-5 w-5 text-foreground" />
                  </button>
                </div>
              </TooltipTrigger>
            </DropdownMenuTrigger>
            <TooltipContent>{t('shapeMenu.more')}</TooltipContent>
          </Tooltip>
          <DropdownMenuContent
            align="end"
            className="w-[200px] max-h-[65vh] overflow-y-auto overscroll-contain"
          >
          {!hideEditAndStyleClipboardForImages && (
            <DropdownMenuItem data-testid="tid.editor.shapeMenu.edit" onSelect={handleMenuEdit}>
              <Pencil className="mr-1" />
              {t('shapeMenu.edit')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem data-testid="tid.editor.shapeMenu.properties" onSelect={() => { handleOpenPropertiesPanel(); }}>
            <PanelRight className="mr-1" />
            {t('floatingProperties.properties')}
          </DropdownMenuItem>
          {!hideEditAndStyleClipboardForImages && (
            <>
              <DropdownMenuItem data-testid="tid.editor.shapeMenu.copyStyle" onSelect={handleCopyStyle}>
                <SwatchBook className="mr-1" />
                {t('shapeMenu.copyStyle')}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="tid.editor.shapeMenu.pasteStyle" onSelect={handlePasteStyle}>
                <ClipboardPaste className="mr-1" />
                {t('shapeMenu.pasteStyle')}
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="tid.editor.shapeMenu.addComment" onSelect={() => { void handleAddComment(); }}>
            <MessageSquarePlus className="mr-1"/>
            {t('shapeMenu.addComment')}
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="tid.editor.shapeMenu.copyLink" onSelect={handleCopyLink}><Link className="mr-1"/>{t('shapeMenu.copyLink')}</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="tid.editor.shapeMenu.lockToggle"
            onSelect={async (e) => {
              // Prevent default to ensure consistent behavior across browsers
              e.preventDefault();
              try {
                const selectedIds = Array.from(effectiveToolContext.selection || new Set<string>());
                if (selectedIds.length === 0) return;
                const cmd = new ChangeNodeStyleCommand(selectedIds, 'locked', !allLocked);
                const commandManager = getCommandManager();
                await commandManager.execute(cmd);
              } catch (err) {
                logger.error('Failed to lock selection', err);
              }
            }}
          >
            {allLocked ? (
              <>
                <Unlock className="mr-1"/>{t('shapeMenu.unlock')}
              </>
            ) : (
              <>
                <Lock className="mr-1"/>{t('shapeMenu.lock')}
              </>
            )}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="tid.editor.shapeMenu.bringToFront" onSelect={() => handleZ('front')}><BringToFront className="mr-1"/>{t('shapeMenu.bringToFront')}</DropdownMenuItem>
          <DropdownMenuItem data-testid="tid.editor.shapeMenu.bringForward" onSelect={() => handleZ('forward')}><MoveUp className="mr-1"/>{t('shapeMenu.bringForward')}</DropdownMenuItem>
          <DropdownMenuItem data-testid="tid.editor.shapeMenu.sendBackward" onSelect={() => handleZ('backward')}><MoveDown className="mr-1"/>{t('shapeMenu.sendBackward')}</DropdownMenuItem>
          <DropdownMenuItem data-testid="tid.editor.shapeMenu.sendToBack" onSelect={() => handleZ('back')}><SendToBack className="mr-1"/>{t('shapeMenu.sendToBack')}</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="tid.editor.shapeMenu.copy" onSelect={() => { void handleCopySelection(); }}><Copy className="mr-1"/>{t('shapeMenu.copy')}</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="tid.editor.shapeMenu.delete" className="text-red-600" onSelect={() => { void handleDeleteSelection(); }}><Trash2 className="mr-1"/>{t('shapeMenu.delete')}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TooltipProvider>
      <ChromeInspector toolRegistry={toolRegistry} />
    </div>
  );
};
